<template>
  <div>
    <input id="product-color" type="hidden" :value="currentColorLabel" />
    <div v-if="swatches.length" class="attribute-color">
      <div class="buying-zone__form-label-group">
        <div class="variantName">
          <span v-html="label" />
          <template v-if="!isSaleSelected">
            <span>/</span>
            <span qaid="product-color-selected" class="text-gray-60 italic" v-html="currentColorLabel" />
            <span v-if="currentFinishValue">; <template v-html="currentFinishLabel" /> / </span>
            <span v-if="currentFinishValue" class="text-gray-60 italic" v-html="currentFinishValue" />
          </template>
        </div>
      </div>
      <pdl-swatch-group
        class="my-1"
        :options="swatches"
        :radio-name="swatchesName"
        :initial-selected-option="selectedColor"
        @color-changed="colorChanged"
      />
    </div>
    <!-- Sale Swatches -->
    <div v-if="saleSwatches.length" class="attribute-color">
      <div class="buying-zone__form-label-group">
        <div class="variantName text-red">
          <span v-html="bp('saleColor')" />
          <template v-if="isSaleSelected">
            <span>/</span>
            <span qaid="product-sale-color-selected" class="text-red italic" v-html="currentColorLabel" />
            <span v-if="currentFinishValue">; <template v-html="currentFinishLabel" /> / </span>
            <span v-if="currentFinishValue" class="text-red italic" v-html="currentFinishValue" />
          </template>
        </div>
      </div>
      <pdl-swatch-group
        class="my-1"
        :options="saleSwatches"
        :radio-name="saleSwatchesName"
        :initial-selected-option="selectedSaleColor"
        @color-changed="colorChanged"
      />
    </div>
    <div v-if="currentColor && children && children.length > 0">
      <component
        :is="getAttributeComponent(children)"
        :base-properties="baseProperties"
        :product="product"
        :init-attribute-selection="initAttributeSelection"
        :label="getAttributeLabel(children)"
        :render-stock-status="renderStockStatus"
        :stock-message="stockMessage"
        :attribute="children"
        @color-changed="
          (color) => {
            $emit('color-changed', color);
          }
        "
        @product-selected="productSelected"
      >
      </component>
    </div>
  </div>
</template>
<script>
import {Attribute} from '@/components/containers/pdp/attributes/mixins/attribute';
import {AttributeRenderer} from '@/components/containers/pdp/attributes/mixins/attribute-renderer';
import {PdlSwatchGroup} from '@pedal/pdl-swatch';
import swatchMixin from '@/mixins/swatches.js';

export default {
  name: 'AttributeColor',
  components: {PdlSwatchGroup},
  mixins: [Attribute, AttributeRenderer, swatchMixin],
  data() {
    return {
      currentColor: null,
      saleSwatchesName: 'sale-swatches',
      swatchesName: 'swatches',
      selectedColor: undefined,
      selectedSaleColor: undefined,
    };
  },
  computed: {
    children() {
      const children = this.getActiveItem()?.children ?? [];

      return children.sort((a, b) => {
        return a.weight > b.weight ? 1 : a.weight < b.weight ? -1 : 0;
      });
    },
    isSaleSelected() {
      return this.currentColor?.isSale ?? false;
    },
    /**
     * Get human label for the current color.
     * @return {*}
     */
    currentColorLabel() {
      if (this.currentColor) {
        return this.getActiveItem()?.options?.displayName ?? '';
      }
      return '';
    },
    /**
     * Retrieve a set of swatch configurations.
     * @return {Array}
     */
    swatches() {
      if (!this.attribute) {
        return [];
      }

      let swatchArr = [];
      const metaItemCodes = this.getAttributeMetadataCodes();

      this.attribute
        .filter((item) => !item.isSale)
        .forEach((item) => {
          if (metaItemCodes.includes(item.code)) {
            const name = item.options?.colorSwatchName;
            swatchArr.push({
              name,
              isSale: false,
              ...this.processColorCode(name),
            });
          }
        });

      return swatchArr;
    },
    saleSwatches() {
      if (!this.attribute) {
        return [];
      }
      const metaItemCodes = this.getAttributeMetadataCodes();
      return this.attribute
        .filter((item) => item.isSale)
        .map((item) => {
          if (metaItemCodes.includes(item.code)) {
            const name = item.options?.colorSwatchName;
            return {
              name,
              isSale: true,
              ...this.processColorCode(name),
            };
          }
        });
    },
    /**
     * Initialize default color from given v-model or selects the first one in the list if nothing has been specified.
     * First searches sale swatches then selects from default group.
     */
    defaultColor() {
      if (!this.swatches?.length) {
        return {};
      }

      const initSwatch =
        this.saleSwatches.find((saleSwatch) => {
          return saleSwatch.name === this.initAttributeSelection[this.attribute[0]?.code];
        }) ||
        this.swatches.find((swatch) => {
          return swatch.name === this.initAttributeSelection[this.attribute[0]?.code];
        });

      if (initSwatch) {
        return initSwatch;
      } else {
        return this.swatches[0];
      }
    },
  },
  mounted() {
    const swatch =
      this.saleSwatches?.find((swatch) => {
        return swatch.name === this.initAttributeSelection[this.attribute[0]?.code];
      }) ||
      this.swatches?.find((swatch) => {
        return swatch.name === this.initAttributeSelection[this.attribute[0]?.code];
      }) ||
      this.swatches?.[0];
    if (swatch.isSale) {
      this.selectedSaleColor = {...swatch, radioName: this.saleSwatchesName};
      this.currentColor = this.selectedSaleColor;
    } else {
      this.selectedColor = {...swatch, radioName: this.swatchesName};
      this.currentColor = this.selectedColor;
    }
    this.fireSelectedProduct();
  },
  methods: {
    fireSelectedProduct() {
      const children = this.getActiveItem()?.children;
      if (children && children.length > 0) {
        return;
      }

      const currentProduct = this.getActiveItem()?.options;
      if (currentProduct) {
        this.productSelected(currentProduct);
      }
    },

    /**
     * Event handler, fires when user changes color via swatches.
     * @param selected
     */
    colorChanged(selected) {
      if (!selected?.name) return;
      this.currentColor = selected;
      this.$emit('color-changed', selected.name);
      if (selected.isSale) {
        this.selectedColor = undefined;
        this.selectedSaleColor = selected;
      } else {
        this.selectedColor = selected;
        this.selectedSaleColor = undefined;
      }
      this.fireSelectedProduct();
    },

    /**
     * Get currently selected attribute.
     */
    getActiveItem() {
      return this.attribute.find((color) => {
        return this.isSaleSelected
          ? color.isSale && color.options?.colorSwatchName === this.currentColor?.name
          : color.options?.colorSwatchName === this.currentColor?.name;
      });
    },
  },
};
</script>
